import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { message, notification } from 'antd';
import { attendanceAPI, biometricAPI } from '../utils/api';
import { useAuth } from './AuthContext';
import { discoverRDPort, rdCaptureLocal, extractTemplateFromXML, verifyFingerprintLocal } from '../utils/biometricLocal';

const BiometricContext = createContext();

export const BiometricProvider = ({ children }) => {
  const { user } = useAuth();
  const [deviceStatus, setDeviceStatus] = useState('checking'); // checking | ready | not_found
  const [connection, setConnection] = useState(null);
  const [scanStatus, setScanStatus] = useState('idle'); // idle | scanning | identifying | success | failed
  const [lastMatchedUser, setLastMatchedUser] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const resetTimer = useRef(null);
  const speechRef = useRef(null);

  // 🔊 VOICE FEEDBACK
  useEffect(() => {
    if ('speechSynthesis' in window) {
      const loadVoices = () => { window.speechSynthesis.getVoices(); };
      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  const speak = useCallback((text) => {
    if (!('speechSynthesis' in window)) return;

    // 🔊 Stability Delay
    setTimeout(() => {
      try {
        window.speechSynthesis.cancel();
        window.speechSynthesis.resume();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.lang = 'en-IN';
        
        // Voice discovery
        const voices = window.speechSynthesis.getVoices();
        const inVoice = voices.find(v => v.lang === 'en-IN') || 
                        voices.find(v => v.lang.includes('IN')) || 
                        voices[0];
        if (inVoice) utterance.voice = inVoice;

        // 🛡️ CHROME FIX: Keep reference and use a heartbeat while speaking
        speechRef.current = utterance;
        
        // If it's a long sentence, Chrome might stop. We force resume.
        utterance.onstart = () => {
          const resumeInterval = setInterval(() => {
            if (!window.speechSynthesis.speaking) {
              clearInterval(resumeInterval);
            } else {
              window.speechSynthesis.resume();
            }
          }, 5000);
        };

        utterance.onerror = (event) => {
          console.error('Speech error:', event);
          // If error occurs, try one more time after a reset
          if (event.error === 'not-allowed') {
             message.warning("Audio blocked. Please click anywhere to enable sound.");
          }
        };

        window.speechSynthesis.speak(utterance);
      } catch (err) {
        console.error('Speech critical error:', err);
      }
    }, 150);
  }, []);

  const detectDevice = useCallback(async () => {
    setDeviceStatus('checking');
    try {
      // 1. Try local hardware first (Direct Browser -> 127.0.0.1)
      const localConn = await discoverRDPort();
      if (localConn) {
        setConnection(localConn);
        setDeviceStatus('ready');
        return;
      }

      // 2. Fallback to server proxy (only works if backend is local)
      const res = await biometricAPI.rdStatus();
      if (res.data.status === 'ready') {
        setDeviceStatus('ready');
      } else {
        setDeviceStatus('not_found');
      }
    } catch {
      setDeviceStatus('not_found');
    }
  }, []);

  // Only detect device when user is logged in, and only once per session
  useEffect(() => {
    if (user && deviceStatus === 'checking') {
      detectDevice();
    }
  }, [user]); // Only runs when user changes (login/logout)

  const scheduleReset = useCallback((delay = 5000) => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => {
      setScanStatus('idle');
      setLastMatchedUser(null);
    }, delay);
  }, []);

  const handleScan = useCallback(async () => {
    if (deviceStatus !== 'ready' || scanStatus === 'scanning' || scanStatus === 'identifying' || isPaused) return;

    setScanStatus('scanning');
    try {
      let result;
      
      if (connection) {
        // 🚀 REMOTE FIX: Capture locally, fetch templates, and match locally!
        const pidOptions = `<?xml version="1.0"?><PidOptions ver="1.0"><Opts fCount="1" fType="0" iCount="0" pCount="0" format="0" pidVer="2.0" timeout="10000" posh="UNKNOWN" env="P"/></PidOptions>`;
        const capturedXml = await rdCaptureLocal(connection, pidOptions);
        
        // Check if capture was successful
        if (!capturedXml.includes('errCode="0"') && !capturedXml.includes("errCode='0'")) {
          setScanStatus('idle');
          return;
        }

        setScanStatus('identifying');
        
        // Extract raw base64 template from the captured XML
        const probTemplate = extractTemplateFromXML(capturedXml);

        // Fetch all templates from the backend
        const templatesRes = await biometricAPI.getAllTemplates(user?.wellness_center_id);
        const templates = templatesRes.data.templates || [];

        let matchedConsumer = null;

        // Perform 1:N matching locally via MorFinAuth
        for (const cand of templates) {
          const isMatch = await verifyFingerprintLocal(connection, probTemplate, cand.template);
          if (isMatch) {
             matchedConsumer = cand;
             break;
          }
        }

        if (matchedConsumer) {
          result = { data: { matched: true, consumer: matchedConsumer } };
        } else {
          result = { data: { matched: false } };
        }
      } else {
        // Legacy: Let server handle everything (only if backend is local)
        result = await biometricAPI.rdIdentify({ center_id: user?.wellness_center_id });
        setScanStatus('identifying');
      }

      if (result.data?.matched && result.data?.consumer) {
        const matchedConsumer = result.data.consumer;
        setLastMatchedUser(matchedConsumer);

        try {
          await attendanceAPI.checkIn({ consumer_id: matchedConsumer.id });
          setScanStatus('success');
          speak(`Welcome ${matchedConsumer.name}. Attendance marked.`);
          
          notification.success({
            message: 'Biometric Attendance',
            description: `${matchedConsumer.name} checked in successfully.`,
            placement: 'topRight',
            duration: 4,
          });
          
          scheduleReset(5000);
        } catch (err) {
          const errMsg = err.response?.data?.error || '';
          if (errMsg.includes('already checked in') || errMsg.includes('completed attendance')) {
            setScanStatus('success'); 
            speak(`${matchedConsumer.name}, you are already checked in.`);
            notification.info({
              message: 'Biometric Attendance',
              description: `${matchedConsumer.name} is already checked in.`,
              placement: 'topRight',
            });
          } else {
            setScanStatus('failed');
            speak("Invalid check-in. Please try again.");
          }
          scheduleReset(4000);
        }
      } else {
        setScanStatus('failed');
        speak("Member not registered. Please contact the administrator.");
        notification.error({
          message: 'Biometric Attendance',
          description: 'This fingerprint is not registered in our system.',
          placement: 'topRight',
          duration: 4,
        });
        scheduleReset(3000);
      }
    } catch (err) {
      setScanStatus('idle');
    }
  }, [deviceStatus, scanStatus, isPaused, speak, scheduleReset]);

  // Global Loop - Only run when user is logged in
  useEffect(() => {
    let timer;
    if (user && deviceStatus === 'ready' && scanStatus === 'idle' && !isPaused) {
      timer = setTimeout(() => {
        handleScan();
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [user, deviceStatus, scanStatus, isPaused, handleScan]);

  // Reset biometric state when user logs out
  useEffect(() => {
    if (!user) {
      // Clear all biometric state on logout
      setDeviceStatus('checking');
      setConnection(null);
      setScanStatus('idle');
      setLastMatchedUser(null);
      setIsPaused(false);
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
      // Cancel any ongoing speech
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    }
  }, [user]);

  return (
    <BiometricContext.Provider value={{ 
      deviceStatus, 
      scanStatus, 
      lastMatchedUser, 
      detectDevice,
      speak,
      isPaused,
      setIsPaused 
    }}>
      {children}
    </BiometricContext.Provider>
  );
};

export const useBiometric = () => useContext(BiometricContext);
